import { GenericLoading } from 'components/ui/Loader';
import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { RoleNames, isSameRoleName } from 'core/roles';
import { PLATEFORME_CHOICE_PENDING_KEY, isChoiceStillValid } from 'features/auth/plateformeChoiceMarker';

/** Payload JWT (base64url) — null si token illisible. */
const decodeJwtPayload = (token?: string): Record<string, unknown> | null => {
  try {
    if (!token) return null;
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

/**
 * Accueil selon le RÔLE ACTIF du token (claim `active_role_name` — contrat back
 * « un rôle à la fois ») : actif admin -> back-office direct, sinon /bord.
 * Repli si la claim manque (vieux token) : admin dans la liste `role`.
 * Claim illisible = /bord (comportement historique).
 */
const homeFromToken = (accessToken?: string): string => {
  const payload = decodeJwtPayload(accessToken);
  const activeRole = payload?.active_role_name;
  if (typeof activeRole === 'string' && activeRole) {
    return isSameRoleName(activeRole, RoleNames.Admin) ? '/back-office/home' : '/bord';
  }
  const claim = payload?.role ?? payload?.roles;
  const roles = Array.isArray(claim) ? claim : typeof claim === 'string' ? [claim] : [];
  return roles.some((r) => isSameRoleName(String(r), RoleNames.Admin))
    ? '/back-office/home'
    : '/bord';
};

const CallbackPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  // Idempotence : un double montage / re-render ne doit pas retraiter le callback.
  const handledRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (handledRef.current) return;
      try {
        if (auth.isAuthenticated) {
          handledRef.current = true;
          // Login OK : réarmer l'auto-retry « No matching state » pour la prochaine fois
          sessionStorage.removeItem('oidcStateRetry');
          const redirectUrl = sessionStorage.getItem('redirectUrl') || '';
          sessionStorage.removeItem('redirectUrl');
          // Une vraie page profonde mémorisée (session expirée en cours de
          // navigation) est restaurée telle quelle ; les accueils (vide, /, /bord —
          // typiquement mémorisés après un logout) sont décidés par le rôle du token.
          const isHomeLike =
            !redirectUrl || redirectUrl === '/' || redirectUrl === '/bord';
          // Connexion standard (mot de passe) : PlateformeChoiceGate déclare la
          // connexion au back et fait choisir la plateforme si le compte en a
          // plusieurs. Le callback SSO (SsoCallback) ne pose jamais ce marqueur.
          // Pas de marqueur si le choix a été fait il y a moins de 12 heures
          // (nouvel onglet = reconnexion silencieuse qui repasse par ici).
          if (!isChoiceStillValid(auth.user?.profile.sub)) {
            sessionStorage.setItem(PLATEFORME_CHOICE_PENDING_KEY, '1');
          }
          navigate(
            isHomeLike ? homeFromToken(auth.user?.access_token) : redirectUrl,
            { replace: true }
          );
        } else if (auth.error) {
          handledRef.current = true;
          // Diagnostic prod : l'erreur était avalée silencieusement (console muette).
          // NE PAS purger le storage ici : la clé oidc.<state> est la pièce à
          // conviction (« No matching state ») et le storage OIDC ne doit jamais
          // être nettoyé sur la route de callback.
          console.error('[OIDC callback]', auth.error);

          // « No matching state » = le flow n'est pas parti de l'app (favori sur la
          // page de login IdP avec un vieux ReturnUrl/state) : le state du callback
          // n'existe pas dans le storage. La session IdP, elle, vient d'être posée →
          // on relance UN signinRedirect propre (invisible pour l'utilisateur).
          // Garde anti-boucle en sessionStorage : un seul auto-retry par tentative.
          const isStateMismatch = /no matching state/i.test(auth.error.message);
          const alreadyRetried = sessionStorage.getItem('oidcStateRetry') === '1';
          if (isStateMismatch && !alreadyRetried) {
            sessionStorage.setItem('oidcStateRetry', '1');
            await auth.signinRedirect();
            return;
          }

          navigate('/login-error', {
            replace: true,
            state: { errorMessage: auth.error.message, errorName: auth.error.name }
          });
        }
      } catch (error) {
        handledRef.current = true;
        console.error('[OIDC callback]', error);
        navigate('/login-error', {
          replace: true,
          state: { errorMessage: error instanceof Error ? error.message : String(error) }
        });
      }
    };
    handleCallback();
  }, [auth.isAuthenticated, auth.isLoading, auth.error, auth.user, navigate]);

  // Afficher un loader pendant le traitement du callback
  return (
    <GenericLoading loading={true} isFullPage />
  );
};

export default CallbackPage;
